import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cog, Award, TrendingDown, ChevronDown, ChevronUp } from "lucide-react";
import api from "../../lib/api";

/** Side-by-side comparator that calls /api/quotes/:id/compare-machines and
 * renders a sortable table with delta vs the cheapest option. Helps the
 * workshop owner justify investing in a new machine ("CMZ TC 66 saves €X/batch")
 * or recommend the right machine for the batch size.
 */
export default function MachineCompareSection({ quoteId, currencySymbol = "€" }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post(`/quotes/${quoteId}/compare-machines`);
      setData(data);
    } catch (e) {
      setError(t("compare.errorGeneric", { defaultValue: "Impossibile caricare il confronto." }));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && !data) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rows = data?.comparisons || [];
  const fmtCurrency = (v) => `${currencySymbol}${(Number(v) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtMin = (v) => `${(Number(v) || 0).toFixed(1)} min`;

  return (
    <div className="border border-slate-700 rounded-md overflow-hidden" data-testid="machine-compare-section">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-900/60 hover:bg-slate-900 transition-colors"
        data-testid="compare-toggle"
      >
        <div className="flex items-center gap-2">
          <TrendingDown size={16} className="text-blue-400" />
          <span className="font-display font-semibold text-slate-100">
            {t("compare.title", { defaultValue: "Confronto macchine" })}
          </span>
          <span className="text-xs text-slate-500 hidden sm:inline">
            {t("compare.subtitle", { defaultValue: "Costo e tempo ciclo con ogni macchina disponibile" })}
          </span>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {open && (
        <div className="bg-slate-900/40 p-4">
          {loading && (
            <div className="text-sm text-slate-400" data-testid="compare-loading">
              {t("compare.loading", { defaultValue: "Calcolo confronto in corso..." })}
            </div>
          )}
          {error && (
            <div className="px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-xs">{error}</div>
          )}
          {!loading && !error && rows.length === 0 && data && (
            <div className="text-sm text-slate-500" data-testid="compare-empty">
              {t("compare.empty", { defaultValue: "Nessuna macchina disponibile per il confronto." })}
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto" data-testid="compare-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs label-tech text-slate-500 border-b border-slate-800">
                    <th className="py-2 pr-3">{t("compare.colMachine", { defaultValue: "Macchina" })}</th>
                    <th className="py-2 px-3 text-right">{t("compare.colRate", { defaultValue: "€/h" })}</th>
                    <th className="py-2 px-3 text-right">{t("compare.colCycle", { defaultValue: "Tempo ciclo" })}</th>
                    <th className="py-2 px-3 text-right">{t("compare.colTotal", { defaultValue: "Totale batch" })}</th>
                    <th className="py-2 pl-3 text-right">{t("compare.colDelta", { defaultValue: "Δ vs migliore" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const isBest = idx === 0;
                    const deltaCost = r.delta_cost_vs_best_pct;
                    const deltaTime = r.delta_time_vs_best_pct;
                    return (
                      <tr
                        key={r.user_machine_id || r.category_id || r.label}
                        className={`border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors ${isBest ? "bg-emerald-500/5" : ""}`}
                        data-testid={`compare-row-${idx}`}
                      >
                        <td className="py-3 pr-3">
                          <div className="flex items-start gap-2">
                            {isBest ? (
                              <Award size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Cog size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <div className="text-slate-100 font-medium truncate">{r.label}</div>
                              <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2">
                                <span className={r.kind === "catalog" ? "text-amber-400" : "text-emerald-400"}>
                                  {r.kind === "catalog"
                                    ? t("compare.kindCatalog", { defaultValue: "Catalogo" })
                                    : t("compare.kindWorkshop", { defaultValue: "Officina" })}
                                </span>
                                {r.reasons && r.reasons.length > 0 && (
                                  <span className="text-emerald-300/80">{r.reasons.slice(0, 2).join(" · ")}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right font-mono-data text-slate-300">{fmtCurrency(r.hourly_rate)}</td>
                        <td className="py-3 px-3 text-right font-mono-data text-slate-300">{fmtMin(r.total_minutes_per_piece)}</td>
                        <td className={`py-3 px-3 text-right font-mono-data ${isBest ? "text-emerald-300 font-semibold" : "text-slate-100"}`}>
                          {fmtCurrency(r.total_cost_with_margin)}
                        </td>
                        <td className="py-3 pl-3 text-right">
                          {isBest ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300">
                              {t("compare.best", { defaultValue: "Migliore" })}
                            </span>
                          ) : (
                            <div className="text-xs">
                              <div className={`font-mono-data ${deltaCost > 0 ? "text-red-400" : "text-emerald-400"}`}>
                                {deltaCost > 0 ? "+" : ""}{deltaCost}%
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {deltaTime > 0 ? "+" : ""}{deltaTime}% {t("compare.time", { defaultValue: "tempo" })}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 text-xs text-slate-500">
                {t("compare.footer", { defaultValue: "Il calcolo applica il moltiplicatore di efficienza inferito dalle caratteristiche di ogni macchina (doppia torretta, contromandrino, multi-asse, ecc)." })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
